/* Frost — main product section behaviour (sections/frost-pdp-main.liquid):
   sticky offset under the fixed header, gallery/zoom, variant selection
   (price/stock/availability/URL sync), quantity, one-open-at-a-time
   dropdowns, size-guide drawer, buy now, pincode estimate, offer-code copy,
   share/wishlist and the sticky add-to-cart bar. Add to cart itself goes
   through <product-form> (assets/product-form.js) so the cart drawer /
   notification keeps working as everywhere else in the theme. */
(function () {
  var COUNT_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

  function init() {
    document.querySelectorAll('[data-fz-pdp]').forEach(function (root) {
      if (root.dataset.fzPdpInit) return;
      root.dataset.fzPdpInit = 'true';

      var ctx = { root: root };
      initStickyTop(ctx);
      initGallery(ctx);
      initToasts(ctx);
      initQuantity(ctx);
      initVariants(ctx);
      initStockLimit(ctx);
      initAccordions(ctx);
      initSizeGuide(ctx);
      initAddons(ctx);
      initBuyNow(ctx);
      initPincode(ctx);
      initOfferCopy(ctx);
      initShare(ctx);
      initWishlist(ctx);
      initStickyBar(ctx);
    });
  }

  /* ---------- sticky columns sit just under the fixed header ---------- */
  function initStickyTop(ctx) {
    var root = ctx.root;
    var selectors = '.frost-header, .orizza-header, .utility-bar, .announcement-bar-vertical, sticky-header, header-sticky, .shopify-section-header-sticky';
    var raf = null;
    var basePadding = parseFloat(root.style.paddingTop) || 0;
    var lastOverlap = -1;

    function measure() {
      raf = null;
      var bottom = 0;
      var fixedBottom = 0;
      document.querySelectorAll(selectors).forEach(function (el) {
        var pos = getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') return;
        var rect = el.getBoundingClientRect();
        if (rect.height === 0 || rect.bottom <= 0 || rect.top > window.innerHeight / 2) return;
        if (rect.bottom > bottom) bottom = rect.bottom;
        if (pos === 'fixed' && rect.bottom > fixedBottom) fixedBottom = rect.bottom;
      });
      root.style.setProperty('--fz-pdp-top', Math.round(bottom + 16) + 'px');

      // If the page doesn't reserve room for the fixed header, the section
      // would start underneath it — push the content down by the overlap.
      var docTop = root.getBoundingClientRect().top + window.scrollY;
      var overlap = Math.max(0, Math.round(fixedBottom - docTop));
      if (overlap !== lastOverlap) {
        lastOverlap = overlap;
        root.style.paddingTop = (basePadding + overlap) + 'px';
      }
    }
    function schedule() { if (!raf) raf = requestAnimationFrame(measure); }

    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('load', schedule);
  }

  /* ---------- gallery ---------- */
  function initGallery(ctx) {
    var root = ctx.root;
    var track = root.querySelector('[data-fz-track]');
    var slides = root.querySelectorAll('[data-fz-slide]');
    var thumbs = root.querySelectorAll('[data-fz-thumb]');
    var rail = root.querySelector('[data-fz-rail]');
    var progress = root.querySelector('[data-fz-progress]');
    var current = 0;

    function isHorizontal() {
      return track && getComputedStyle(track).flexDirection === 'row';
    }

    function setActive(index) {
      current = index;
      thumbs.forEach(function (t) {
        var on = Number(t.dataset.index) === index;
        t.classList.toggle('is-active', on);
        if (on && rail) {
          // keep the active thumb visible inside the (scrollable) rail without
          // scrolling the page itself
          var horizontal = rail.scrollWidth > rail.clientWidth + 1;
          if (horizontal) {
            var left = t.offsetLeft - (rail.clientWidth - t.offsetWidth) / 2;
            rail.scrollTo({ left: left, behavior: 'smooth' });
          } else if (rail.scrollHeight > rail.clientHeight + 1) {
            var top = t.offsetTop - (rail.clientHeight - t.offsetHeight) / 2;
            rail.scrollTo({ top: top, behavior: 'smooth' });
          }
        }
      });
    }

    function updateProgress() {
      if (!progress || !track) return;
      var ratio;
      if (isHorizontal()) {
        var maxX = track.scrollWidth - track.clientWidth;
        ratio = maxX > 0 ? track.scrollLeft / maxX : 0;
      } else {
        var maxY = track.scrollHeight - track.clientHeight;
        ratio = maxY > 0 ? track.scrollTop / maxY : 0;
      }
      progress.style.setProperty('--fz-progress', Math.max(0, Math.min(1, ratio)).toFixed(4));
    }

    function goTo(index, smooth) {
      if (!track || !slides.length) return;
      index = Math.max(0, Math.min(index, slides.length - 1));
      var slide = slides[index];
      if (isHorizontal()) {
        track.scrollTo({ left: slide.offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
      } else {
        track.scrollTo({ top: slide.offsetTop, behavior: smooth === false ? 'auto' : 'smooth' });
      }
      setActive(index);
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { goTo(Number(t.dataset.index)); });
    });

    if (track) {
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          updateProgress();
          var index = isHorizontal()
            ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth))
            : Math.round(track.scrollTop / Math.max(1, track.clientHeight));
          if (index !== current) setActive(index);
        });
      }, { passive: true });

      track.addEventListener('keydown', function (e) {
        var next = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
        if (!next) return;
        e.preventDefault();
        goTo(current + next);
      });
    }

    // expose so a variant change can jump to its image
    ctx.goToMedia = function (mediaId) {
      if (!mediaId) return;
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].dataset.mediaId) === String(mediaId)) { goTo(i); return; }
      }
    };

    initZoom(ctx, slides, function () { return current; }, goTo);
  }

  function initZoom(ctx, slides, getCurrent, goTo) {
    var root = ctx.root;
    var zoom = root.querySelector('[data-fz-zoom]');
    if (!zoom || !slides.length) return;
    var img = zoom.querySelector('[data-fz-zoom-img]');
    var index = 0;
    var lastFocus = null;

    function sourceFor(i) {
      var el = slides[i] && slides[i].querySelector('img');
      if (!el) return null;
      // largest candidate from srcset
      var best = el.currentSrc || el.src;
      var set = el.getAttribute('srcset');
      if (set) {
        var parts = set.split(',').map(function (p) { return p.trim().split(/\s+/); });
        parts.sort(function (a, b) { return parseInt(b[1], 10) - parseInt(a[1], 10); });
        if (parts[0] && parts[0][0]) best = parts[0][0];
      }
      return { src: best, alt: el.alt || '' };
    }

    function show(i) {
      index = (i + slides.length) % slides.length;
      var s = sourceFor(index);
      if (!s) return;
      img.src = s.src;
      img.alt = s.alt;
    }

    function open(i) {
      lastFocus = document.activeElement;
      show(i);
      zoom.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var closeBtn = zoom.querySelector('[data-fz-zoom-close]');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      zoom.hidden = true;
      document.documentElement.style.overflow = '';
      goTo(index, false);
      if (lastFocus) lastFocus.focus();
    }

    slides.forEach(function (slide, i) {
      if (!slide.querySelector('img')) return;
      slide.addEventListener('click', function () {
        // on phones the track swipes horizontally; a tap still opens zoom
        open(i);
      });
    });

    zoom.addEventListener('click', function (e) {
      if (e.target === zoom || e.target === img) close();
    });
    var prev = zoom.querySelector('[data-fz-zoom-prev]');
    var next = zoom.querySelector('[data-fz-zoom-next]');
    var closeBtn = zoom.querySelector('[data-fz-zoom-close]');
    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); show(index - 1); });
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); show(index + 1); });
    if (closeBtn) closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    if (slides.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }
    document.addEventListener('keydown', function (e) {
      if (zoom.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(index - 1);
      else if (e.key === 'ArrowRight') show(index + 1);
    });
  }

  /* ---------- quantity ---------- */
  function initQuantity(ctx) {
    var root = ctx.root;
    var input = root.querySelector('[data-fz-qty-input]');
    ctx.qtyInput = input;
    if (!input) return;

    function clamp(value) {
      var min = Number(input.min) || 1;
      var max = input.max !== '' && input.max != null ? Number(input.max) : Infinity;
      var step = Number(input.step) || 1;
      value = Math.round(value / step) * step;
      return Math.max(min, Math.min(value, max));
    }

    ctx.stepQty = function (dir) {
      var step = Number(input.step) || 1;
      var current = Number(input.value) || 1;

      // + at the stock limit: don't add, show the popup instead
      if (dir > 0) {
        var limit = ctx.stockLimit ? ctx.stockLimit() : Infinity;
        if (isFinite(limit) && current + step > limit) {
          if (ctx.toast) ctx.toast((ctx.toastText || '').split('[count]').join(String(limit)));
          return;
        }
      }

      input.value = clamp(current + dir * step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    var minus = root.querySelector('[data-fz-qty-minus]');
    var plus = root.querySelector('[data-fz-qty-plus]');
    if (minus) minus.addEventListener('click', function () { ctx.stepQty(-1); });
    if (plus) plus.addEventListener('click', function () { ctx.stepQty(1); });

    input.addEventListener('change', function () {
      var raw = Number(input.value) || 1;
      var clamped = clamp(raw);
      if (String(clamped) !== input.value) input.value = clamped;

      // typed more than what's in stock: snap back and tell them
      var limit = ctx.stockLimit ? ctx.stockLimit() : Infinity;
      if (raw > limit && ctx.toast) {
        ctx.toast((ctx.toastText || '').split('[count]').join(String(limit)));
      }

      if (ctx.checkStock) ctx.checkStock();
    });
  }

  /* ---------- stock: cap the quantity, dim +, hide qty when sold out ---------- */
  function initStockLimit(ctx) {
    var root = ctx.root;
    var variantsEl = root.querySelector('[data-fz-variants]');
    var variantInput = root.querySelector('[data-fz-variant-input]');
    var addBtn = root.querySelector('[data-fz-add]');
    var variants = [];
    try { variants = variantsEl ? JSON.parse(variantsEl.textContent) : []; } catch (e) { variants = []; }

    if (ctx.variant === undefined) {
      var id = variantInput ? Number(variantInput.value) : null;
      ctx.variant = variants.filter(function (v) { return v.id === id; })[0] || null;
      ctx.available = addBtn ? !addBtn.disabled : !!ctx.variant;
    }

    function stockLimit(variant) {
      if (!variant) return Infinity;
      var tracked = variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue';
      return tracked ? variant.inventoryQuantity : Infinity;
    }

    ctx.stockLimit = function () { return stockLimit(ctx.variant); };

    ctx.checkStock = function () {
      var available = !!ctx.available;

      // buy buttons are only disabled when the variant can't be bought at all
      ['[data-fz-add]', '[data-fz-buy-now]', '[data-fz-sticky-add]'].forEach(function (sel) {
        root.querySelectorAll(sel).forEach(function (b) { b.disabled = !available; });
      });

      // sold out / unavailable: hide the quantity selector (buy box + sticky bar)
      root.querySelectorAll('.fz-pdp-qtyblock, .fz-pdp-qty--sticky').forEach(function (el) {
        el.style.display = available ? '' : 'none';
      });

      var input = ctx.qtyInput;
      if (!input) return;

      var min = Number(input.min) || 1;
      var max = Infinity;
      if (ctx.variant && ctx.variant.quantityRuleMax != null) max = ctx.variant.quantityRuleMax;
      max = Math.min(max, stockLimit(ctx.variant));

      if (isFinite(max)) input.max = max;
      else input.removeAttribute('max');

      var value = Number(input.value) || min;
      if (isFinite(max) && value > max) value = Math.max(min, max);
      if (value < min) value = min;
      if (String(value) !== input.value) input.value = value;

      root.querySelectorAll('[data-fz-sticky-qty]').forEach(function (el) { el.textContent = input.value; });

      // + looks disabled at the stock limit but stays clickable (click shows the popup)
      var step = Number(input.step) || 1;
      root.querySelectorAll('[data-fz-qty-plus], [data-fz-sticky-plus]').forEach(function (b) {
        var atLimit = value + step > max;
        b.classList.toggle('is-limit', atLimit);
        b.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      });
      root.querySelectorAll('[data-fz-qty-minus], [data-fz-sticky-minus]').forEach(function (b) {
        b.disabled = value <= min;
      });
    };

    ctx.checkStock();
  }

  /* ---------- toast (slides in from the right, auto-dismisses) ---------- */
  function initToasts(ctx) {
    var root = ctx.root;
    var host = root.querySelector('[data-fz-toasts]');
    if (!host) return;
    // move to <body> so no transformed/clipped ancestor can trap the fixed box
    if (host.parentNode !== document.body) document.body.appendChild(host);

    ctx.toastText = host.dataset.text || '';
    var duration = Math.max(1, Number(host.dataset.duration) || 4);
    var current = null;

    function dismiss(toast) {
      if (!toast || toast.classList.contains('is-leaving')) return;
      toast.classList.add('is-leaving');
      var done = function () { if (toast.parentNode) toast.parentNode.removeChild(toast); };
      toast.addEventListener('animationend', function (e) { if (e.target === toast) done(); });
      setTimeout(done, 900);
      if (current === toast) current = null;
    }

    ctx.toast = function (message) {
      if (!message) return;
      var top = getComputedStyle(root).getPropertyValue('--fz-pdp-top').trim();
      if (top) host.style.top = top;
      // one at a time: a new message replaces the one on screen and restarts its timer
      if (current) {
        current.querySelector('.fz-pdp-toast-text').textContent = message;
        var bar = current.querySelector('.fz-pdp-toast-bar');
        bar.style.animation = 'none';
        void bar.offsetWidth;
        bar.style.animation = '';
        return;
      }

      var toast = document.createElement('div');
      toast.className = 'fz-pdp-toast';
      toast.setAttribute('role', 'alert');
      toast.style.setProperty('--fz-toast-time', duration + 's');
      toast.innerHTML =
        '<svg class="fz-pdp-toast-icon" viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="11" fill="currentColor"/>' +
        '<rect x="10.75" y="5.5" width="2.5" height="8.5" rx="1.25" fill="var(--fz-toast-bg)"/>' +
        '<circle cx="12" cy="17.6" r="1.5" fill="var(--fz-toast-bg)"/></svg>' +
        '<span class="fz-pdp-toast-text"></span>' +
        '<button type="button" class="fz-pdp-toast-close" aria-label="Close">&times;</button>' +
        '<span class="fz-pdp-toast-bar" aria-hidden="true"></span>';
      toast.querySelector('.fz-pdp-toast-text').textContent = message;
      toast.querySelector('.fz-pdp-toast-close').addEventListener('click', function () { dismiss(toast); });
      // the bar's shrink animation is the timer, so hovering pauses both
      toast.querySelector('.fz-pdp-toast-bar').addEventListener('animationend', function () { dismiss(toast); });
      host.appendChild(toast);
      current = toast;
    };

    // cart errors from <product-form> show as a toast instead of the inline box
    var wrapper = root.querySelector('.product-form__error-message-wrapper');
    if (wrapper && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (wrapper.hidden) return;
        var msg = wrapper.querySelector('.product-form__error-message');
        if (msg && msg.textContent.trim()) ctx.toast(msg.textContent.trim());
        wrapper.hidden = true;
      }).observe(wrapper, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
    }
  }

  /* ---------- variants ---------- */
  function initVariants(ctx) {
    var root = ctx.root;
    var variantsEl = root.querySelector('[data-fz-variants]');
    var variants = [];
    try { variants = variantsEl ? JSON.parse(variantsEl.textContent) : []; } catch (e) { variants = []; }
    var groups = root.querySelectorAll('[data-fz-option]');
    if (!groups.length || !variants.length) return;

    var selected = [];
    groups.forEach(function (g) {
      var on = g.querySelector('.is-active');
      selected[Number(g.dataset.index)] = on ? on.dataset.fzValue : null;
    });

    function matches(v, sel) {
      return sel.every(function (val, i) { return val == null || v.options[i] === val; });
    }

    function refreshAvailability() {
      groups.forEach(function (g) {
        var idx = Number(g.dataset.index);
        g.querySelectorAll('[data-fz-value]').forEach(function (btn) {
          var trial = selected.slice();
          trial[idx] = btn.dataset.fzValue;
          var available = variants.some(function (v) { return v.available && matches(v, trial); });
          btn.classList.toggle('is-out', !available);
        });
      });
    }

    function onPick(group, btn) {
      var idx = Number(group.dataset.index);
      selected[idx] = btn.dataset.fzValue;
      group.querySelectorAll('[data-fz-value]').forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      var label = root.querySelector('[data-fz-option-selected="' + idx + '"]');
      if (label) label.textContent = btn.dataset.fzValue;

      var variant = variants.filter(function (v) { return matches(v, selected); })[0];
      refreshAvailability();
      applyVariant(ctx, variant);
      if (variant && variant.featuredMediaId && ctx.goToMedia) ctx.goToMedia(variant.featuredMediaId);
      if (variant && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    groups.forEach(function (g) {
      g.querySelectorAll('[data-fz-value]').forEach(function (btn) {
        btn.addEventListener('click', function () { onPick(g, btn); });
      });
    });

    refreshAvailability();
  }

  function isQuantityRuleSoldOut(variant) {
    var tracked = variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue';
    return tracked && variant.quantityRuleMin > variant.inventoryQuantity;
  }

  function applyVariant(ctx, variant) {
    var root = ctx.root;
    var variantInput = root.querySelector('[data-fz-variant-input]');
    var addBtn = root.querySelector('[data-fz-add]');
    var buyBtn = root.querySelector('[data-fz-buy-now]');
    var addLabel = addBtn ? addBtn.querySelector('span') : null;
    var soldOutText = root.dataset.soldOutText || 'Sold out';
    var comingSoonText = root.dataset.comingSoonText || 'Coming soon';
    var addText = addBtn ? addBtn.dataset.label || 'Add to cart' : 'Add to cart';

    // variants without "Track quantity" in Shopify are Coming soon
    var comingSoon = !!variant && !variant.inventoryManagement;
    var available = !!variant && variant.available && !comingSoon && !isQuantityRuleSoldOut(variant);
    ctx.variant = variant || null;
    ctx.available = available;
    // Coming soon: hide the Razorpay Buy Now so the Coming soon button takes the full row.
    var ctaRow = root.querySelector('[data-fz-cta-row]');
    if (ctaRow) ctaRow.classList.toggle('fz-pdp-cta--coming-soon', comingSoon);
    var labelText = !variant ? 'Unavailable' : (comingSoon ? comingSoonText : (available ? addText : soldOutText));

    if (variantInput && variant) variantInput.value = variant.id;
    if (variantInput) variantInput.disabled = !available;
    [addBtn, buyBtn].forEach(function (b) { if (b) b.disabled = !available; });
    if (addLabel) addLabel.textContent = labelText;

    root.querySelectorAll('[data-fz-sticky-add]').forEach(function (b) { b.disabled = !available; });
    root.querySelectorAll('[data-fz-sticky-label]').forEach(function (el) { el.textContent = labelText; });

    // No add-ons for a sold-out piece; untick them so none ride along later.
    root.querySelectorAll('[data-fz-addons]').forEach(function (box) {
      box.hidden = !available;
      if (!available) {
        box.querySelectorAll('[data-fz-addon]:checked').forEach(function (input) {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });

    if (!variant) {
      if (ctx.checkStock) ctx.checkStock();
      return;
    }

    root.querySelectorAll('[data-fz-price]').forEach(function (el) { el.textContent = variant.price; });
    root.querySelectorAll('[data-fz-compare]').forEach(function (el) {
      el.textContent = variant.compareAtPrice || '';
      el.hidden = !variant.compareAtPrice;
    });
    root.querySelectorAll('[data-fz-off]').forEach(function (el) {
      el.textContent = variant.offPct ? variant.offPct + '% OFF' : '';
      el.hidden = !variant.offPct;
    });

    var stickyImg = root.querySelector('[data-fz-sticky-thumb] img');
    if (stickyImg && variant.image) {
      stickyImg.removeAttribute('srcset');
      stickyImg.src = variant.image;
    }

    updateStock(root, variant);

    var qty = ctx.qtyInput;
    if (qty) {
      var min = variant.quantityRuleMin || 1;
      qty.min = min;
      qty.step = variant.quantityRuleIncrement || 1;
      qty.value = min; // switching variant always starts again from the minimum (1)
    }
    if (ctx.checkStock) ctx.checkStock();
  }

  function updateStock(root, variant) {
    var el = root.querySelector('[data-fz-stock]');
    if (!el) return;
    var threshold = Number(el.dataset.threshold || 5);
    var qty = variant.inventoryQuantity;
    var show = variant.inventoryManagement === 'shopify' && qty > 0 && qty <= threshold;
    el.hidden = !show;
    if (!show) return;
    var countText = String(qty);
    if (el.dataset.words === 'true' && qty >= 0 && qty <= 10) countText = COUNT_WORDS[qty];
    var text = qty === 1 && el.dataset.textSingle ? el.dataset.textSingle : (el.dataset.text || '').split('[count]').join(countText);
    var textEl = el.querySelector('[data-fz-stock-text]');
    if (textEl) textEl.textContent = text;
  }

  /* ---------- dropdowns: only one open at a time ---------- */
  function initAccordions(ctx) {
    ctx.root.querySelectorAll('[data-fz-accordions]').forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('[data-fz-acc]'));

      function setOpen(item, open) {
        var body = item.querySelector('[data-fz-acc-body]');
        var btn = item.querySelector('[data-fz-acc-btn]');
        if (!body || !btn) return;
        var isOpen = item.classList.contains('is-open');
        if (open === isOpen) return;

        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          item.classList.add('is-open');
          body.style.maxHeight = '0px';
          // eslint-disable-next-line no-unused-expressions
          body.offsetHeight;
          body.style.maxHeight = body.scrollHeight + 'px';
          var onEnd = function (e) {
            if (e.target !== body) return;
            body.removeEventListener('transitionend', onEnd);
            if (item.classList.contains('is-open')) body.style.maxHeight = 'none';
          };
          body.addEventListener('transitionend', onEnd);
        } else {
          body.style.maxHeight = body.scrollHeight + 'px';
          // eslint-disable-next-line no-unused-expressions
          body.offsetHeight;
          item.classList.remove('is-open');
          body.style.maxHeight = '0px';
        }
      }

      items.forEach(function (item) {
        var btn = item.querySelector('[data-fz-acc-btn]');
        if (!btn) return;
        btn.addEventListener('click', function () {
          var willOpen = !item.classList.contains('is-open');
          items.forEach(function (other) { if (other !== item) setOpen(other, false); });
          setOpen(item, willOpen);
        });
      });
    });
  }

  /* ---------- size guide drawer ---------- */
  function initSizeGuide(ctx) {
    var root = ctx.root;
    var drawer = root.querySelector('[data-fz-guide]');
    if (!drawer) return;
    var panel = drawer.querySelector('[role="dialog"]');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      if (panel) setTimeout(function () { panel.focus(); }, 50);
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    root.querySelectorAll('[data-fz-guide-open]').forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelectorAll('[data-fz-guide-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ---------- add-ons ----------
     Ticked add-ons go into the cart in the same request as the product:
     the form data is switched to Shopify's multi-item format
     (items[n][id] / items[n][quantity]) just before it's sent. */
  function selectedAddons(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-fz-addon]:checked'));
  }

  // Cart fields for each ticked add-on. The hallmark certificate is linked to
  // the chosen variant (`_hallmark_for`) and matches its quantity, the same
  // shape the side cart creates (see assets/side-cart.js).
  function addonFields(addons, variantId, qty) {
    var fields = [];
    addons.forEach(function (input, i) {
      var prefix = 'items[' + (i + 1) + ']';
      var isHallmark = input.hasAttribute('data-fz-addon-hallmark');
      fields.push([prefix + '[id]', input.value]);
      fields.push([prefix + '[quantity]', isHallmark ? qty : 1]);
      if (isHallmark) fields.push([prefix + '[properties][_hallmark_for]', variantId]);
    });
    return fields;
  }

  function toItemsFormData(form, addons) {
    var data = new FormData(form);
    if (!addons.length) return data;
    var id = data.get('id');
    var qty = data.get('quantity') || 1;
    data.delete('id');
    data.delete('quantity');
    data.append('items[0][id]', id);
    data.append('items[0][quantity]', qty);
    addonFields(addons, id, qty).forEach(function (field) {
      data.append(field[0], field[1]);
    });
    return data;
  }

  function initAddons(ctx) {
    var root = ctx.root;
    var inputs = root.querySelectorAll('[data-fz-addon]');
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      var tile = input.closest('.fz-pdp-addon');
      input.addEventListener('change', function () { if (tile) tile.classList.toggle('is-on', input.checked); });
    });

    // <product-form> builds its request with `new FormData(form)` inside its
    // own submit handler. For just that moment (this capture-phase listener
    // runs first) swap the plain id/quantity fields for items[] fields, then
    // put everything back.
    var productForm = root.querySelector('product-form.fz-pdp-form');
    var form = productForm && productForm.querySelector('form');
    if (!form) return;
    productForm.addEventListener('submit', function () {
      var addons = selectedAddons(root);
      if (!addons.length) return;
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = document.querySelector('[name="quantity"][form="' + form.id + '"]') || form.querySelector('[name="quantity"]');
      var added = [];
      function hidden(name, value) {
        var el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        el.value = value;
        form.appendChild(el);
        added.push(el);
      }
      var qty = qtyInput ? qtyInput.value : 1;
      hidden('items[0][id]', idInput.value);
      hidden('items[0][quantity]', qty);
      addonFields(addons, idInput.value, qty).forEach(function (field) {
        hidden(field[0], field[1]);
      });
      var idWasDisabled = idInput.disabled;
      idInput.disabled = true;
      if (qtyInput) qtyInput.disabled = true;
      setTimeout(function () {
        added.forEach(function (el) { el.remove(); });
        idInput.disabled = idWasDisabled;
        if (qtyInput) qtyInput.disabled = false;
      }, 0);
    }, true);
  }

  /* ---------- buy now: add to cart, then straight to checkout ---------- */
  function initBuyNow(ctx) {
    var root = ctx.root;
    var buyBtn = root.querySelector('[data-fz-buy-now]');
    if (!buyBtn) return;

    buyBtn.addEventListener('click', function () {
      if (buyBtn.disabled) return;
      var form = root.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || !window.routes || !window.routes.cart_add_url) return;

      var productForm = buyBtn.closest('product-form');
      var errorWrapper = productForm ? productForm.querySelector('.product-form__error-message-wrapper') : null;
      var errorMessage = errorWrapper ? errorWrapper.querySelector('.product-form__error-message') : null;
      function showError(message) {
        if (ctx.toast) { ctx.toast(message); return; }
        if (!errorWrapper || !errorMessage) return;
        errorMessage.textContent = message;
        errorWrapper.hidden = false;
      }

      if (errorWrapper) errorWrapper.hidden = true;
      buyBtn.disabled = true;
      buyBtn.classList.add('loading');

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        body: toItemsFormData(form, selectedAddons(root)),
        headers: { Accept: 'application/json' },
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.status) {
            showError(data.description || data.message || 'This item could not be added to your cart.');
            buyBtn.disabled = false;
            buyBtn.classList.remove('loading');
            return;
          }
          window.location.href = (window.shopUrl || '') + '/checkout';
        })
        .catch(function () {
          showError('Something went wrong. Please try again.');
          buyBtn.disabled = false;
          buyBtn.classList.remove('loading');
        });
    });
  }

  /* ---------- pincode (settings-based estimate) ---------- */
  function initPincode(ctx) {
    var root = ctx.root;
    var btn = root.querySelector('[data-fz-pin-check]');
    var input = root.querySelector('[data-fz-pin-input]');
    var msg = root.querySelector('[data-fz-pin-msg]');
    var eta = root.querySelector('[data-fz-eta]');
    if (!btn || !input) return;

    function fmt(d) { return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); }

    function check() {
      var value = (input.value || '').trim();
      if (!/^[1-9][0-9]{5}$/.test(value)) {
        if (msg) {
          msg.hidden = false;
          msg.classList.add('is-error');
          msg.textContent = btn.dataset.errorText || 'Enter a valid 6-digit pincode';
        }
        if (eta) eta.hidden = true;
        return;
      }
      var today = new Date();
      var dispatch = new Date(today); dispatch.setDate(today.getDate() + Number(btn.dataset.dispatchDays || 1));
      var deliver = new Date(today); deliver.setDate(today.getDate() + Number(btn.dataset.deliveryDays || 5));
      if (msg) {
        msg.hidden = false;
        msg.classList.remove('is-error');
        msg.textContent = btn.dataset.successText || 'Delivery available at this pincode';
      }
      if (eta) {
        eta.hidden = false;
        eta.querySelector('[data-fz-eta-order]').textContent = fmt(today);
        eta.querySelector('[data-fz-eta-dispatch]').textContent = fmt(dispatch);
        eta.querySelector('[data-fz-eta-deliver]').textContent = fmt(deliver);
      }
    }

    btn.addEventListener('click', check);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); check(); } });
    input.addEventListener('input', function () { input.value = input.value.replace(/\D/g, '').slice(0, 6); });
  }

  /* ---------- offer code copy ---------- */
  function initOfferCopy(ctx) {
    ctx.root.querySelectorAll('[data-fz-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.dataset.fzCopy;
        var label = btn.querySelector('[data-fz-copy-label]');
        if (!code || !navigator.clipboard) return;
        navigator.clipboard.writeText(code).then(function () {
          if (!label) return;
          label.textContent = 'Copied';
          setTimeout(function () { label.textContent = code; }, 1600);
        }).catch(function () {});
      });
    });
  }

  /* ---------- share / wishlist ---------- */
  function initShare(ctx) {
    ctx.root.querySelectorAll('[data-fz-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = window.location.href;
        if (navigator.share) {
          navigator.share({ url: url, title: document.title }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).catch(function () {});
        }
      });
    });
  }

  function initWishlist(ctx) {
    ctx.root.querySelectorAll('[data-fz-wishlist]').forEach(function (btn) {
      var key = 'fz-wishlist';
      var id = String(btn.dataset.productId);
      function read() {
        try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { return []; }
      }
      function paint(on) {
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      paint(read().indexOf(id) !== -1);
      btn.addEventListener('click', function () {
        var list = read();
        var i = list.indexOf(id);
        if (i === -1) list.push(id); else list.splice(i, 1);
        try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
        paint(i === -1);
      });
    });
  }

  /* ---------- sticky add to cart ---------- */
  function initStickyBar(ctx) {
    var root = ctx.root;
    var bar = root.querySelector('[data-fz-sticky]');
    var cta = root.querySelector('[data-fz-cta]');
    var mainAdd = root.querySelector('[data-fz-add]');
    if (!bar || !cta || !mainAdd) return;

    function setVisible(on) {
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.querySelectorAll('button').forEach(function (b) { b.tabIndex = on ? 0 : -1; });
      document.body.classList.toggle('fz-pdp-sticky-on', on);
    }

    // shown whenever the main add-to-cart buttons are out of view (above OR
    // below, incl. scrolled out of the buy box's own scroll area), hidden
    // while they're on screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        setVisible(!entries[0].isIntersecting);
      }).observe(cta);
    } else {
      setVisible(true);
    }

    bar.querySelectorAll('[data-fz-sticky-add]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled || mainAdd.disabled) return;
        mainAdd.click();
      });
    });
    bar.querySelectorAll('[data-fz-sticky-minus]').forEach(function (b) {
      b.addEventListener('click', function () { if (ctx.stepQty) ctx.stepQty(-1); });
    });
    bar.querySelectorAll('[data-fz-sticky-plus]').forEach(function (b) {
      b.addEventListener('click', function () { if (ctx.stepQty) ctx.stepQty(1); });
    });

    // rotating message tab on the mobile bar (last row duplicates the first,
    // so the loop resets without a visible jump)
    var tick = bar.querySelector('.fz-pdp-sticky-tick.is-animated');
    if (tick && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      var rows = tick.children.length;
      var step = 0;
      setInterval(function () {
        step += 1;
        tick.style.transition = '';
        tick.style.transform = 'translateY(' + (-27 * step) + 'px)';
        if (step === rows - 1) {
          setTimeout(function () {
            tick.style.transition = 'none';
            tick.style.transform = 'translateY(0)';
            step = 0;
          }, 700);
        }
      }, 3600);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();